import api from './client';

/**
 * Thin wrappers around the API. One module per resource keeps feature code
 * declarative — mirror this file when you add a new resource.
 */
export const AdminsApi = {
  list: (params) => api.get('/admins', { params }).then((r) => r.data),
  get: (id) => api.get(`/admins/${id}`).then((r) => r.data.data),
  create: (payload) => api.post('/admins', payload).then((r) => r.data.data),
  update: (id, payload) => api.put(`/admins/${id}`, payload).then((r) => r.data.data),
  remove: (id) => api.delete(`/admins/${id}`),
  syncPermissions: (id, permissions) =>
    api.put(`/admins/${id}/permissions`, { permissions }).then((r) => r.data.data),
};

export const BranchesApi = {
  list: () => api.get('/branches').then((r) => r.data.data),
  create: (payload) => api.post('/branches', payload).then((r) => r.data.data),
  update: (id, payload) => api.put(`/branches/${id}`, payload).then((r) => r.data.data),
  remove: (id) => api.delete(`/branches/${id}`),
};

export const BrandingApi = {
  get: () => api.get('/branding').then((r) => r.data.data),
  update: (payload) => api.put('/branding', payload).then((r) => r.data.data),
  uploadLogo: (file) => {
    const fd = new FormData();
    fd.append('logo', file);
    return api.post('/branding/logo', fd).then((r) => r.data.data);
  },
  deleteLogo: () => api.delete('/branding/logo').then((r) => r.data.data),
};

export const ThemesApi = {
  list: () => api.get('/themes').then((r) => r.data.data),
  create: (payload) => api.post('/themes', payload).then((r) => r.data.data),
  update: (id, payload) => api.put(`/themes/${id}`, payload).then((r) => r.data.data),
  remove: (id) => api.delete(`/themes/${id}`),
};

export const PermissionsApi = {
  // -> [{ group, permissions: [name, ...] }]
  catalogue: () => api.get('/permissions').then((r) => r.data.data),
};

export const CrmApi = {
  lookups: () => api.get('/crm/lookups').then((r) => r.data.data),

  leads: {
    list: (params, signal) => api.get('/crm/leads', { params, signal }).then((r) => r.data),
    get: (id) => api.get(`/crm/leads/${id}`).then((r) => r.data.data),
    create: (payload) => api.post('/crm/leads', payload).then((r) => r.data.data),
    update: (id, payload) => api.put(`/crm/leads/${id}`, payload).then((r) => r.data.data),
    remove: (id) => api.delete(`/crm/leads/${id}`),
    // leadIds: number[]; userId: staff id, or null to unassign -> { updated, skipped }
    assign: (leadIds, userId) =>
      api.post('/crm/leads/assign', { lead_ids: leadIds, assigned_to: userId }).then((r) => r.data.data),
  },

  followups: {
    // params: { tab, day_start, day_end, search?, assigned_to?, page } -> { data, meta, counts }
    board: (params, signal) => api.get('/crm/followups', { params, signal }).then((r) => r.data),
    create: (leadId, payload) =>
      api.post(`/crm/leads/${leadId}/followups`, payload).then((r) => r.data.data),
  },

  reasons: {
    list: () => api.get('/crm/reasons').then((r) => r.data.data),
    create: (payload) => api.post('/crm/reasons', payload).then((r) => r.data.data),
    update: (id, payload) => api.put(`/crm/reasons/${id}`, payload).then((r) => r.data.data),
    remove: (id) => api.delete(`/crm/reasons/${id}`),
  },

  statuses: {
    list: () => api.get('/crm/statuses').then((r) => r.data.data),
    create: (payload) => api.post('/crm/statuses', payload).then((r) => r.data.data),
    update: (id, payload) => api.put(`/crm/statuses/${id}`, payload).then((r) => r.data.data),
    remove: (id) => api.delete(`/crm/statuses/${id}`),
  },

  sources: {
    list: () => api.get('/crm/sources').then((r) => r.data.data),
    create: (payload) => api.post('/crm/sources', payload).then((r) => r.data.data),
    update: (id, payload) => api.put(`/crm/sources/${id}`, payload).then((r) => r.data.data),
    remove: (id) => api.delete(`/crm/sources/${id}`),
  },

  // Company website lead form: { enabled, api_key, endpoint_url, assignees, staff, leads_count }
  websiteSettings: {
    get: () => api.get('/crm/website-settings').then((r) => r.data.data),
    save: (payload) => api.put('/crm/website-settings', payload).then((r) => r.data.data),
    regenerateKey: () => api.post('/crm/website-settings/regenerate-key').then((r) => r.data.data),
  },

  // Meta Lead Ads: { assignees: [userId…], staff, webhook_url, configured, leads_count }
  metaSettings: {
    get: () => api.get('/crm/meta-settings').then((r) => r.data.data),
    save: (assignees) => api.put('/crm/meta-settings', { assignees }).then((r) => r.data.data),
  },

  customFields: {
    // entity: 'lead' | 'followup' (omit for all)
    list: (entity) =>
      api.get('/crm/custom-fields', { params: { entity } }).then((r) => r.data.data),
    create: (payload) => api.post('/crm/custom-fields', payload).then((r) => r.data.data),
    update: (id, payload) => api.put(`/crm/custom-fields/${id}`, payload).then((r) => r.data.data),
    remove: (id) => api.delete(`/crm/custom-fields/${id}`),
  },
};

export const WhatsAppApi = {
  // chats
  chats: (params, signal) => api.get('/whatsapp/chats', { params, signal }).then((r) => r.data.data),
  unreadCount: () => api.get('/whatsapp/chats/unread-count').then((r) => r.data.data.count),
  // updatedAfter: server_time from the previous poll; the reply's `updates` lists outbound
  // messages whose delivery status changed since then.
  messages: (chatId, afterId, updatedAfter) =>
    api
      .get(`/whatsapp/chats/${chatId}/messages`, {
        params: afterId ? { after_id: afterId, ...(updatedAfter ? { updated_after: updatedAfter } : {}) } : {},
      })
      .then((r) => r.data.data),
  // payload: plain object; files/arrays are sent as multipart
  send: (chatId, payload) => api.post(`/whatsapp/chats/${chatId}/messages`, toForm(payload)).then((r) => r.data.data),
  initiate: (payload) => api.post('/whatsapp/chats/initiate', toForm(payload)).then((r) => r.data.data),
  assign: (chatId, userId) =>
    api.post(`/whatsapp/chats/${chatId}/assign`, { user_id: userId }).then((r) => r.data.data),
  unassign: (chatId) => api.post(`/whatsapp/chats/${chatId}/unassign`).then((r) => r.data.data),
  toggleBot: (chatId, paused) =>
    api.post(`/whatsapp/chats/${chatId}/bot`, { paused }).then((r) => r.data.data),
  // Creates a lead (source: WhatsApp) from the chat and links it. 409 DUPLICATE_PHONE if the number already has a lead.
  createLead: (chatId, payload) => api.post(`/whatsapp/chats/${chatId}/create-lead`, payload).then((r) => r.data),
  linkLead: (chatId, leadId) => api.post(`/whatsapp/chats/${chatId}/lead`, { lead_id: leadId }).then((r) => r.data.data),
  staffOptions: () => api.get('/whatsapp/chats/staff-options').then((r) => r.data.data),
  mediaBlob: (chatId, messageId) =>
    api
      .get(`/whatsapp/chats/${chatId}/messages/${messageId}/media`, { responseType: 'blob' })
      .then((r) => r.data),

  // templates
  templates: (params) => api.get('/whatsapp/templates', { params }).then((r) => r.data.data),
  createTemplate: (payload) => api.post('/whatsapp/templates', toForm(payload)).then((r) => r.data.data),
  syncTemplates: () => api.post('/whatsapp/templates/sync').then((r) => r.data.data),
  deleteTemplate: (id) => api.delete(`/whatsapp/templates/${id}`),
  templateImageBlob: (id) =>
    api.get(`/whatsapp/templates/${id}/header-image`, { responseType: 'blob' }).then((r) => r.data),

  // bot
  bot: () => api.get('/whatsapp/bot').then((r) => r.data.data),
  botStaff: () => api.get('/whatsapp/bot/staff-options').then((r) => r.data.data),
  saveBotSettings: (payload) => api.put('/whatsapp/bot/settings', payload).then((r) => r.data.data),
  saveRule: (id, payload) =>
    (id ? api.put(`/whatsapp/bot/rules/${id}`, payload) : api.post('/whatsapp/bot/rules', payload)).then(
      (r) => r.data.data
    ),
  deleteRule: (id) => api.delete(`/whatsapp/bot/rules/${id}`),
  uploadRuleMedia: (id, file) =>
    api.post(`/whatsapp/bot/rules/${id}/media`, toForm({ file })).then((r) => r.data.data),
  deleteRuleMedia: (id) => api.delete(`/whatsapp/bot/rules/${id}/media`).then((r) => r.data.data),
  simulate: (payload) => api.post('/whatsapp/bot/simulate', payload).then((r) => r.data.data),
};

/** Object -> FormData. Arrays become key[]; null/undefined/'' are skipped. */
function toForm(obj) {
  const fd = new FormData();
  Object.entries(obj).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') return;
    if (Array.isArray(v)) v.forEach((item) => fd.append(`${k}[]`, item ?? ''));
    else fd.append(k, v);
  });
  return fd;
}

export const CallsApi = {
  list: (params, signal) => api.get('/crm/calls', { params, signal }).then((r) => r.data),
  summary: (params, signal) => api.get('/crm/calls/summary', { params, signal }).then((r) => r.data.data),
  remove: (id) => api.delete(`/crm/calls/${id}`),
  recordingBlob: (id) =>
    api.get(`/crm/call-recordings/${id}/stream`, { responseType: 'blob' }).then((r) => r.data),
  leadCalls: (leadId) => api.get(`/crm/leads/${leadId}/calls`).then((r) => r.data.data),
  linkableCalls: (leadId) => api.get(`/crm/leads/${leadId}/linkable-calls`).then((r) => r.data.data),
};

export const DevicesApi = {
  list: () => api.get('/crm/devices').then((r) => r.data.data),
  forceLogout: (id) => api.post(`/crm/devices/${id}/force-logout`),
  remove: (id) => api.delete(`/crm/devices/${id}`),
};

export const AttendanceApi = {
  // own register (attendance.own)
  today: () => api.get('/attendance/today').then((r) => r.data.data),
  checkIn: () => api.post('/attendance/check-in').then((r) => r.data.data),
  checkOut: () => api.post('/attendance/check-out').then((r) => r.data.data),
  mine: (month) => api.get('/attendance/my', { params: { month } }).then((r) => r.data),
  myLeaves: () => api.get('/attendance/leaves').then((r) => r.data.data),
  requestLeave: (payload) => api.post('/attendance/leaves', payload).then((r) => r.data.data),
  cancelLeave: (id) => api.post(`/attendance/leaves/${id}/cancel`).then((r) => r.data.data),

  // everyone (attendance.view_all / manage_all)
  // -> { data: [{id, name, branch_id}], branches: [{id, name}] } (branches only for all-branch viewers)
  staff: () => api.get('/attendance/staff-options').then((r) => r.data),
  records: (params, signal) => api.get('/attendance/records', { params, signal }).then((r) => r.data),
  saveRecord: (payload) => api.post('/attendance/records', payload).then((r) => r.data.data),
  removeRecord: (id) => api.delete(`/attendance/records/${id}`),
  leaves: (params, signal) => api.get('/attendance/leave-requests', { params, signal }).then((r) => r.data.data),
  reviewLeave: (id, payload) =>
    api.post(`/attendance/leave-requests/${id}/review`, payload).then((r) => r.data.data),
};

export const ComplaintsApi = {
  lookups: () => api.get('/complaints/lookups').then((r) => r.data.data),
  list: (params, signal) => api.get('/complaints', { params, signal }).then((r) => r.data),
  get: (id) => api.get(`/complaints/${id}`).then((r) => r.data.data),
  create: (payload) => api.post('/complaints', payload).then((r) => r.data.data),
  assign: (id, userId) => api.post(`/complaints/${id}/assign`, { assigned_to: userId }).then((r) => r.data.data),
  addAction: (id, payload) => api.post(`/complaints/${id}/actions`, payload).then((r) => r.data.data),

  // master: 'types' (complaint types) | 'actions' (action taken)
  masters: {
    list: (master) => api.get(`/complaints/masters/${master}`).then((r) => r.data.data),
    create: (master, payload) => api.post(`/complaints/masters/${master}`, payload).then((r) => r.data.data),
    update: (master, id, payload) => api.put(`/complaints/masters/${master}/${id}`, payload).then((r) => r.data.data),
    remove: (master, id) => api.delete(`/complaints/masters/${master}/${id}`),
  },
};

export const DashboardApi = {
  // params: { day_start, day_end } -> each KPI card, or null when the viewer lacks its permission
  get: (params) => api.get('/dashboard', { params }).then((r) => r.data.data),
  // funnel by stage, conversion by source, lost reasons (null without lead access)
  leadInsights: () => api.get('/dashboard/lead-insights').then((r) => r.data.data),
  // bounds: ISO instants; one bucket between each pair -> [{ from, leads, won }]
  leadTrend: (bounds) => api.get('/dashboard/lead-trend', { params: { bounds } }).then((r) => r.data.data),
};
